import React, { useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Checkbox, Input } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { toast } from "react-toastify";
import SubmitButton from "components/common/SubmitButton";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { PageTabItemTitle } from "components/tabs/TabItem/PageTabItemTitle";
import {
  addContactCategory,
  updateContactCategory,
} from "services/contactCategoriesService";

const validateForm = (values) => {
  const errors = {};
  if (!values.categoryName?.trim())
    errors.categoryName = "Category Name is required";
  return errors;
};

const additionalLabelFields = Array.from({ length: 10 }, (_, index) => {
  const fieldNo = index + 1;
  return {
    name: `additionalLabel${fieldNo}`,
    label: `Additional Field ${fieldNo}`,
    placeholder: `Label for additional field ${fieldNo}`,
  };
});

const buildPayload = (values) => {
  const payload = {
    categoryName: values.categoryName?.trim() || "",
    categoryDetails: values.categoryDetails?.trim() || "",
    showRef1: Boolean(values.showRef1),
    labelForRef1: values.labelForRef1?.trim() || "",
    showRef2: Boolean(values.showRef2),
    labelForRef2: values.labelForRef2?.trim() || "",
    showRef3: Boolean(values.showRef3),
    labelForRef3: values.labelForRef3?.trim() || "",
    active: values.active !== false,
  };

  additionalLabelFields.forEach((field) => {
    payload[field.name] = values[field.name]?.trim() || "";
  });

  return payload;
};

const ContactLinkTypeModal = (props) => {
  const tabs = ["Contact Category", "Additional Info"];
  const ADD_NEW = !props.data || !props.data.contactCategoryId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const handleSubmit = async (formValues) => {
    setIsSubmitting(true);
    try {
      const payload = buildPayload(formValues);
      if (ADD_NEW) {
        const response = await addContactCategory(payload);
        if (response?.success) {
          toast.success("Contact Category added");
          props.onClose?.(true);
        } else {
          props.onClose?.(false);
        }
      } else {
        const contactCategoryId = props.data.contactCategoryId;
        const response = await updateContactCategory(contactCategoryId, payload);
        if (response?.success) {
          props.onClose?.(true);
          toast.success(
            `Contact Category '${props?.data?.categoryName}' has been updated`
          );
        } else {
          props.onClose?.(false);
        }
      }
    } catch (e) {
      props.onClose?.(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      title={
        ADD_NEW ? "Add Contact Category" : `Edit ${props?.data?.categoryName}`
      }
      onClose={() => props.onClose?.(false)}
      className="dialog-xs"
    >
      <>
        <Form
          initialValues={
            ADD_NEW
              ? {
                  active: true,
                  showRef1: true,
                  showRef2: true,
                  showRef3: true,
                }
              : props?.data
          }
          validator={validateForm}
          onSubmit={handleSubmit}
          render={(formRenderProps) => (
            <div className="d-flex flex-column h-100">
              <FormElement
                style={{
                  flex: "1 1 auto",
                  minHeight: 0,
                }}
              >
                <fieldset className="p-3 px-5">
                  <TabStrip
                    selected={selectedTab}
                    onSelect={(e) => setSelectedTab(e.selected)}
                    className="pagetab"
                  >
                    <TabStripTab
                      title={
                        <PageTabItemTitle
                          title={tabs[0]}
                          index={0}
                          selected={selectedTab}
                        />
                      }
                    >
                      <div
                        className="pt-3 pb-2 pe-2"
                        style={{ height: "52vh", overflowY: "auto" }}
                      >
                        <div className="mb-3">
                          <Label className="label mt-1 mb-2 w-100">
                            Category Name&nbsp;
                          </Label>
                          <Field
                            name="categoryName"
                            component={Input}
                            className="w-100"
                            style={{
                              border:
                                submitAttempted &&
                                formRenderProps.errors?.categoryName
                                  ? "1px solid red"
                                  : undefined,
                            }}
                            maxLength={50}
                            placeholder="Name"
                          />
                        </div>
                        <div className="mb-3">
                          <Label className="label mt-1 mb-2 w-100">
                            Category Details&nbsp;
                          </Label>
                          <Field
                            name="categoryDetails"
                            component={Input}
                            className="w-100"
                            maxLength={50}
                            placeholder="Details"
                          />
                        </div>
                        <div className="mb-3 d-flex align-items-center gap-2">
                          <Label className="label mb-0">Contact on Matter</Label>
                          <hr className="flex-grow-1 m-0" />
                        </div>
                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2 mt-1 mb-2">
                            <Field
                              defaultChecked={true}
                              name="showRef1"
                              component={Checkbox}
                              label=""
                            />
                            <Label className="label mb-0">Label for Reference 1</Label>
                          </div>
                          <Field
                            name="labelForRef1"
                            className="w-100"
                            component={Input}
                            maxLength={50}
                            placeholder="Details"
                          />
                        </div>
                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2 mt-1 mb-2">
                            <Field
                              defaultChecked={true}
                              name="showRef2"
                              component={Checkbox}
                              label=""
                            />
                            <Label className="label mb-0">Label for Reference 2</Label>
                          </div>
                          <Field
                            name="labelForRef2"
                            className="w-100"
                            component={Input}
                            maxLength={50}
                            placeholder="Details"
                          />
                        </div>
                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2 mt-1 mb-2">
                            <Field
                              defaultChecked={true}
                              name="showRef3"
                              component={Checkbox}
                              label=""
                            />
                            <Label className="label mb-0">Label for Reference 3</Label>
                          </div>
                          <Field
                            name="labelForRef3"
                            className="w-100"
                            component={Input}
                            maxLength={50}
                            placeholder="Details"
                          />
                        </div>
                        <Field
                          defaultChecked={true}
                          name="active"
                          component={Checkbox}
                          label="Active"
                        />
                      </div>
                    </TabStripTab>
                    <TabStripTab
                      title={
                        <PageTabItemTitle
                          title={tabs[1]}
                          index={1}
                          selected={selectedTab}
                        />
                      }
                    >
                      <div
                        className="pt-3 pb-2 pe-2"
                        style={{ height: "52vh", overflowY: "auto" }}
                      >
                        {additionalLabelFields.map((field) => (
                          <div className="mb-3" key={field.name}>
                            <Label className="label mt-1 mb-2 w-100">
                              {field.label}
                            </Label>
                            <Field
                              name={field.name}
                              component={Input}
                              className="w-100"
                              maxLength={100}
                              placeholder={field.placeholder}
                            />
                          </div>
                        ))}
                      </div>
                    </TabStripTab>
                  </TabStrip>
                </fieldset>
              </FormElement>

              <div className="dialog-footer">
                <button
                  className="bgBtnSecondary common-btn me-2"
                  onClick={() => props.onClose?.(false)}
                  type="button"
                >
                  Cancel
                </button>
                <SubmitButton
                  className="common-btn me-3"
                  onClick={() => {
                    setSubmitAttempted(true);
                    formRenderProps.onSubmit();
                  }}
                  isLoading={isSubmitting}
                  disabled={!formRenderProps.allowSubmit || isSubmitting}
                >
                  {ADD_NEW ? "Add" : "Update"}
                </SubmitButton>
              </div>
            </div>
          )}
        />
      </>
    </Dialog>
  );
};

export default ContactLinkTypeModal;
